import * as React from 'react'

import Classroom from './classroom'
import UnarchiveClassroomModal from './unarchive_classroom_modal'

import ArticleSpotlight from '../shared/articleSpotlight'
import { MY_CLASSES_FEATURED_BLOG_POST_ID } from '../../constants/featuredBlogPost'
import { defaultSnackbarTimeout, getPageTitle, Snackbar } from '../../../Shared/index'
import useSnackbarMonitor from '../../../Shared/hooks/useSnackbarMonitor';
import { requestGet } from '../../../../modules/request/index'

export interface ArchivedClassroomsProps {
  classrooms: any[]
  user: any
}

export const unarchiveClassroomModal = 'unarchiveClassroomModal'

const ArchivedClassrooms: React.FC<ArchivedClassroomsProps> = ({
  classrooms: initialClassrooms,
  user,
}) => {
  const [classrooms, setClassrooms] = React.useState(initialClassrooms.filter((c) => !c.visible))
  const [selectedClassroomId, setSelectedClassroomId] = React.useState<number|null>(null)
  const [showModal, setShowModal] = React.useState<string | null>(null)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarCopy, setSnackbarCopy] = React.useState<string>('')

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  React.useEffect(() => {
    document.title = getPageTitle('Manage Classes', 'Archived Classes')
  }, [])

  const clickClassroomHeader = (classroomId: number) => {
    setSelectedClassroomId(selectedClassroomId === classroomId ? null : classroomId)
  }

  const closeModal = (callback?: () => void): void => {
    setShowModal(null)
    if (callback) callback()
  }

  const getClassrooms = (): void => {
    requestGet('/teachers/classrooms', (body) =>
      setClassrooms(body.classrooms.filter((c) => !c.visible)),
    )
  }

  const onSuccess = (copy: string): void => {
    getClassrooms()
    showSnackbarMessage(copy)
    closeModal()
  }

  const openModal = (modalName: string): void => {
    setShowModal(modalName)
  }

  const showSnackbarMessage = (copy: string): void => {
    setSnackbarCopy(copy)
    setShowSnackbar(true)
  }

  const renderPageContent = () => {
    const ownArchivedClassrooms = classrooms.filter((c) => {
      const owner = c.teachers.find(
        (t: any) => t.classroom_relation === 'owner',
      )
      return !c.visible && owner?.id === user.id
    })

    if (classrooms.length === 0) {
      return (
        <div className="no-archived-classes">
          <h2>No archived classes</h2>
          <p>When you archive a class, you will see it listed here.</p>
        </div>
      )
    }

    return (
      <div className="archived-classes">
        {classrooms.map((c) => (
          <Classroom
            classroom={c}
            classrooms={ownArchivedClassrooms}
            clickClassroomHeader={clickClassroomHeader}
            isOwnedByCurrentUser={!!ownArchivedClassrooms.find((oc) => oc.id === c.id)}
            key={c.id}
            onSuccess={onSuccess}
            selected={c.id === selectedClassroomId}
            unarchiveClass={() => openModal(unarchiveClassroomModal)}
            user={user}
          />
        ))}
      </div>
    )
  }

  return (
    <React.Fragment>
      {showModal === unarchiveClassroomModal && (
        <UnarchiveClassroomModal
          classroom={classrooms.find((c) => c.id === selectedClassroomId)!}
          close={closeModal}
          onSuccess={onSuccess}
        />
      )}
      <Snackbar text={snackbarCopy} visible={showSnackbar} />
      <main
        className="archived-classrooms classrooms-page container gray-background-accommodate-footer"
        id="post-secondary-nav"
      >
        <div className="header">
          <h1>Archived Classes</h1>
        </div>
        {renderPageContent()}
      </main>
      <ArticleSpotlight blogPostId={MY_CLASSES_FEATURED_BLOG_POST_ID} />
    </React.Fragment>
  )
}

export default ArchivedClassrooms
